/**
 * Correction Invoice Form Page (E-56, P-INV-1)
 * Helyesbito szamla letrehozasa — haromsoros modszer (ADR-057)
 */

import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useInvoice, useInvoiceMutations } from '@/hooks/use-invoices';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface CorrectionItemState {
  lineNumber: number;
  description: string;
  originalQuantity: number;
  originalUnitPrice: number;
  originalNetAmount: number;
  newQuantity: number;
  newUnitPrice: number;
  vatPercent: number;
  selected: boolean;
}

/** P-INV-1: Three-line preview row */
interface ThreeLinePreviewRow {
  type: 'ORIGINAL_NEGATED' | 'CORRECTED' | 'DIFFERENCE';
  description: string;
  quantity: number;
  unitPrice: number;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
  lineNumber: number;
}

function formatPrice(price: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * P-INV-1: Check if VAT self-audit may be needed
 * (original and correction in different months)
 */
function checkVatSelfAudit(originalDate: string): {
  required: boolean;
  originalMonth: string;
  correctionMonth: string;
} {
  const orig = new Date(originalDate);
  const now = new Date();
  const originalMonth = `${orig.getFullYear()}-${String(orig.getMonth() + 1).padStart(2, '0')}`;
  const correctionMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return {
    required: originalMonth !== correctionMonth,
    originalMonth,
    correctionMonth,
  };
}

export function CorrectionFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { invoice, isLoading, error } = useInvoice(id);
  const mutations = useInvoiceMutations();

  const [reason, setReason] = useState('');
  const [items, setItems] = useState<CorrectionItemState[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize items from the loaded invoice
  if (invoice && !isInitialized) {
    const initialItems: CorrectionItemState[] = (invoice.items ?? []).map(item => ({
      lineNumber: item.lineNumber,
      description: item.description,
      originalQuantity: item.quantity,
      originalUnitPrice: Number(item.unitPriceNet),
      originalNetAmount: item.quantity * Number(item.unitPriceNet),
      newQuantity: item.quantity,
      newUnitPrice: Number(item.unitPriceNet),
      vatPercent: item.vatPercentage,
      selected: false,
    }));
    setItems(initialItems);
    setIsInitialized(true);
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <p className="text-gray-500 dark:text-gray-400">{t('invoice.loading')}</p>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('invoice.notFound')}
          </h1>
          {error && <p className="mt-2 text-red-600 dark:text-red-400">{error}</p>}
          <Button onClick={() => navigate('/invoices')} className="mt-4">
            {t('common.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  const updateItem = (
    index: number,
    field: 'newQuantity' | 'newUnitPrice' | 'selected',
    value: number | boolean
  ) => {
    setItems(prev => {
      const newItems = [...prev];
      const item = newItems[index];
      if (!item) return prev;
      newItems[index] = { ...item, [field]: value };
      if (field === 'newQuantity' || field === 'newUnitPrice') {
        newItems[index] = { ...newItems[index]!, selected: true };
      }
      return newItems;
    });
  };

  const selectedItems = items.filter(item => item.selected);

  const calculateDiffs = () => {
    let totalNetDiff = 0;
    let totalVatDiff = 0;

    for (const item of selectedItems) {
      const originalNet = item.originalQuantity * item.originalUnitPrice;
      const newNet = item.newQuantity * item.newUnitPrice;
      const netDiff = newNet - originalNet;
      const vatDiff = Math.round(netDiff * (item.vatPercent / 100));
      totalNetDiff += netDiff;
      totalVatDiff += vatDiff;
    }

    return {
      totalNetDiff,
      totalVatDiff,
      totalGrossDiff: totalNetDiff + totalVatDiff,
    };
  };

  const diffs = calculateDiffs();

  const hasChanges = selectedItems.some(
    item =>
      item.newQuantity !== item.originalQuantity || item.newUnitPrice !== item.originalUnitPrice
  );

  // P-INV-1: Generate three-line preview items
  const threeLinePreview: ThreeLinePreviewRow[] = useMemo(() => {
    const rows: ThreeLinePreviewRow[] = [];
    const changedItems = selectedItems.filter(
      item =>
        item.newQuantity !== item.originalQuantity || item.newUnitPrice !== item.originalUnitPrice
    );

    for (const item of changedItems) {
      const originalNet = item.originalQuantity * item.originalUnitPrice;
      const originalVat = Math.round(originalNet * (item.vatPercent / 100));
      const newNet = item.newQuantity * item.newUnitPrice;
      const newVat = Math.round(newNet * (item.vatPercent / 100));
      const netDiff = newNet - originalNet;
      const vatDiff = newVat - originalVat;

      // Line 1: Original NEGATED
      rows.push({
        type: 'ORIGINAL_NEGATED',
        description: `${t('invoice.correction.originalItem')}: ${item.description}`,
        quantity: -item.originalQuantity,
        unitPrice: item.originalUnitPrice,
        netAmount: -originalNet,
        vatAmount: -originalVat,
        grossAmount: -(originalNet + originalVat),
        lineNumber: item.lineNumber,
      });

      // Line 2: Corrected POSITIVE
      rows.push({
        type: 'CORRECTED',
        description: `${t('invoice.correction.correctedItem')}: ${item.description}`,
        quantity: item.newQuantity,
        unitPrice: item.newUnitPrice,
        netAmount: newNet,
        vatAmount: newVat,
        grossAmount: newNet + newVat,
        lineNumber: item.lineNumber,
      });

      // Line 3: Difference
      rows.push({
        type: 'DIFFERENCE',
        description: `${t('invoice.correction.differenceItem')}: ${item.description}`,
        quantity: 1,
        unitPrice: netDiff,
        netAmount: netDiff,
        vatAmount: vatDiff,
        grossAmount: netDiff + vatDiff,
        lineNumber: item.lineNumber,
      });
    }

    return rows;
  }, [selectedItems, t]);

  // P-INV-1: VAT self-audit flag
  const vatAudit = useMemo(() => {
    return checkVatSelfAudit(invoice.invoiceDate);
  }, [invoice.invoiceDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (reason.trim().length < 5) {
      alert(t('invoice.correction.reasonValidation'));
      return;
    }

    if (!hasChanges) {
      alert(t('error.atLeastOneChange'));
      return;
    }

    setIsSubmitting(true);
    try {
      const correctionItems = selectedItems
        .filter(
          item =>
            item.newQuantity !== item.originalQuantity ||
            item.newUnitPrice !== item.originalUnitPrice
        )
        .map(item => {
          const result: { lineNumber: number; quantity?: number; unitPriceNet?: number } = {
            lineNumber: item.lineNumber,
          };
          if (item.newQuantity !== item.originalQuantity) {
            result.quantity = item.newQuantity;
          }
          if (item.newUnitPrice !== item.originalUnitPrice) {
            result.unitPriceNet = item.newUnitPrice;
          }
          return result;
        });

      const correction = await mutations.createCorrection(invoice.id, {
        reason: reason.trim(),
        items: correctionItems,
      });

      alert(t('invoice.correction.correctionCreated', { number: correction.invoiceNumber }));
      navigate(`/invoices/${correction.id}`);
    } catch (err) {
      alert(
        t('invoice.correction.errorPrefix', {
          message: err instanceof Error ? err.message : t('invoice.unknownError'),
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(`/invoices/${invoice.id}`)}>
              &larr; {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('invoice.correction.correctionTitle')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('invoice.correction.originalLabel', { number: invoice.invoiceNumber })}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !hasChanges || reason.trim().length < 5}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {isSubmitting
              ? t('invoice.correction.creating')
              : t('invoice.correction.createCorrection')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* P-INV-1: VAT self-audit warning banner */}
          {vatAudit.required && hasChanges && (
            <Card className="border-orange-300 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <span className="text-xl text-orange-600" aria-hidden="true">
                    !
                  </span>
                  <div>
                    <p className="font-medium text-orange-800 dark:text-orange-200">
                      {t('invoice.correction.vatSelfAudit')}
                    </p>
                    <p className="mt-1 text-sm text-orange-700 dark:text-orange-300">
                      {t('invoice.correction.vatSelfAuditDescription', {
                        originalMonth: vatAudit.originalMonth,
                        correctionMonth: vatAudit.correctionMonth,
                      })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Original invoice info */}
          <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20">
            <CardContent className="pt-4">
              <div className="grid gap-4 sm:grid-cols-4">
                <div>
                  <span className="text-sm text-amber-700 dark:text-amber-300">
                    {t('invoice.correction.originalInvoice')}
                  </span>
                  <p className="font-mono font-medium text-amber-900 dark:text-amber-100">
                    {invoice.invoiceNumber}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-amber-700 dark:text-amber-300">
                    {t('invoice.correction.partner')}
                  </span>
                  <p className="font-medium text-amber-900 dark:text-amber-100">
                    {invoice.partner?.name ?? '-'}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-amber-700 dark:text-amber-300">
                    {t('invoice.correction.originalAmount')}
                  </span>
                  <p className="font-medium text-amber-900 dark:text-amber-100">
                    {formatPrice(Number(invoice.grossAmount))}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-amber-700 dark:text-amber-300">
                    {t('invoice.correction.issuedAt')}
                  </span>
                  <p className="font-medium text-amber-900 dark:text-amber-100">
                    {new Date(invoice.invoiceDate).toLocaleDateString('hu-HU')}
                  </p>
                </div>
              </div>
              {/* P-INV-1: Performance date note */}
              <div className="mt-3 rounded bg-amber-100 px-3 py-2 dark:bg-amber-800/30">
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  {t('invoice.correction.performanceDateNote')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Reason */}
          <Card>
            <CardHeader>
              <CardTitle>{t('invoice.correction.reason')}</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={3}
                required
                minLength={5}
                className="w-full rounded-md border border-gray-300 bg-white p-3 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                placeholder={t('invoice.correction.reasonPlaceholder')}
              />
              {reason.length > 0 && reason.trim().length < 5 && (
                <p className="mt-1 text-sm text-red-500">
                  {t('invoice.correction.reasonMinLength')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Items to modify */}
          <Card>
            <CardHeader>
              <CardTitle>{t('invoice.correction.itemChanges')}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b bg-gray-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('invoice.description')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('invoice.correction.originalQty')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('invoice.correction.newQty')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('invoice.correction.originalPrice')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('invoice.correction.newPrice')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('invoice.correction.difference')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-slate-700">
                    {items.map((item, index) => {
                      const originalNet = item.originalQuantity * item.originalUnitPrice;
                      const newNet = item.newQuantity * item.newUnitPrice;
                      const netDiff = newNet - originalNet;
                      const isChanged =
                        item.newQuantity !== item.originalQuantity ||
                        item.newUnitPrice !== item.originalUnitPrice;

                      return (
                        <tr
                          key={item.lineNumber}
                          className={isChanged ? 'bg-amber-50 dark:bg-amber-900/10' : ''}
                        >
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {item.description}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-[#ccfbf1]">
                              {t('invoice.vat')}: {item.vatPercent}%
                            </p>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                            {item.originalQuantity}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Input
                              type="number"
                              min={0}
                              step={1}
                              value={item.newQuantity}
                              onChange={e =>
                                updateItem(index, 'newQuantity', Number(e.target.value))
                              }
                              className="w-20 text-right ml-auto"
                            />
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                            {formatPrice(item.originalUnitPrice)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Input
                              type="number"
                              min={0}
                              value={item.newUnitPrice}
                              onChange={e =>
                                updateItem(index, 'newUnitPrice', Number(e.target.value))
                              }
                              className="w-28 text-right ml-auto"
                            />
                          </td>
                          <td
                            className={`px-4 py-3 text-right font-medium ${
                              netDiff < 0
                                ? 'text-red-600 dark:text-red-400'
                                : netDiff > 0
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-muted-foreground'
                            }`}
                          >
                            {netDiff !== 0 ? formatPrice(netDiff) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* P-INV-1: Three-line preview */}
          {threeLinePreview.length > 0 && (
            <Card className="border-blue-200 dark:border-blue-800">
              <CardHeader>
                <CardTitle>{t('invoice.correction.previewTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b bg-blue-50 dark:bg-blue-900/30">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-blue-700 dark:text-blue-300">
                          {t('invoice.description')}
                        </th>
                        <th className="px-4 py-2 text-right font-medium text-blue-700 dark:text-blue-300">
                          {t('invoice.quantity')}
                        </th>
                        <th className="px-4 py-2 text-right font-medium text-blue-700 dark:text-blue-300">
                          {t('invoice.unitPrice')}
                        </th>
                        <th className="px-4 py-2 text-right font-medium text-blue-700 dark:text-blue-300">
                          {t('invoice.correction.netDifference')}
                        </th>
                        <th className="px-4 py-2 text-right font-medium text-blue-700 dark:text-blue-300">
                          {t('invoice.vat')}
                        </th>
                        <th className="px-4 py-2 text-right font-medium text-blue-700 dark:text-blue-300">
                          {t('common.total')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-slate-700">
                      {threeLinePreview.map((row, idx) => {
                        const bgClass =
                          row.type === 'ORIGINAL_NEGATED'
                            ? 'bg-red-50 dark:bg-red-900/10'
                            : row.type === 'CORRECTED'
                              ? 'bg-green-50 dark:bg-green-900/10'
                              : 'bg-blue-50 dark:bg-blue-900/10 font-semibold';
                        const textClass =
                          row.type === 'ORIGINAL_NEGATED'
                            ? 'text-red-700 dark:text-red-300'
                            : row.type === 'CORRECTED'
                              ? 'text-green-700 dark:text-green-300'
                              : 'text-blue-700 dark:text-blue-300';

                        return (
                          <tr key={`${row.lineNumber}-${row.type}-${idx}`} className={bgClass}>
                            <td className={`px-4 py-2 ${textClass}`}>{row.description}</td>
                            <td className={`px-4 py-2 text-right ${textClass}`}>{row.quantity}</td>
                            <td className={`px-4 py-2 text-right ${textClass}`}>
                              {formatPrice(row.unitPrice)}
                            </td>
                            <td className={`px-4 py-2 text-right ${textClass}`}>
                              {formatPrice(row.netAmount)}
                            </td>
                            <td className={`px-4 py-2 text-right ${textClass}`}>
                              {formatPrice(row.vatAmount)}
                            </td>
                            <td className={`px-4 py-2 text-right ${textClass}`}>
                              {formatPrice(row.grossAmount)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Difference summary */}
          <Card
            className={diffs.totalGrossDiff !== 0 ? 'border-amber-200 dark:border-amber-800' : ''}
          >
            <CardContent className="pt-6">
              <div className="flex justify-end">
                <div className="w-72 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('invoice.correction.netDifference')}
                    </span>
                    <span
                      className={`font-medium ${
                        diffs.totalNetDiff < 0
                          ? 'text-red-600'
                          : diffs.totalNetDiff > 0
                            ? 'text-green-600'
                            : ''
                      }`}
                    >
                      {formatPrice(diffs.totalNetDiff)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('invoice.correction.vatDifference')}
                    </span>
                    <span
                      className={`font-medium ${
                        diffs.totalVatDiff < 0
                          ? 'text-red-600'
                          : diffs.totalVatDiff > 0
                            ? 'text-green-600'
                            : ''
                      }`}
                    >
                      {formatPrice(diffs.totalVatDiff)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-bold dark:border-slate-700">
                    <span className="text-gray-900 dark:text-white">
                      {t('invoice.correction.grossDifference')}
                    </span>
                    <span
                      className={
                        diffs.totalGrossDiff < 0
                          ? 'text-red-600'
                          : diffs.totalGrossDiff > 0
                            ? 'text-green-600'
                            : 'text-muted-foreground'
                      }
                    >
                      {formatPrice(diffs.totalGrossDiff)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/invoices/${invoice.id}`)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !hasChanges || reason.trim().length < 5}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isSubmitting
                ? t('invoice.correction.creating')
                : t('invoice.correction.createCorrection')}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
